import { mountLoginPage } from "@/auth/login-page";
import { resolvePostLoginPath, waitForAuth } from "@/auth/google-auth";
import { ensurePlayerCloudSynced } from "@/cloud/player-cloud-save";
import { flushPlayerCloudPushNow } from "@/cloud/player-cloud-scheduler";
import { mountCharacterCreatePage } from "@/character/create-page";
import { mountPetSheetGridPage } from "@/pet/mount-pet-sheet-grid-page";
import { mountShadowSheetGridPage } from "@/shadow/mount-shadow-sheet-grid-page";
import { loadCharacterProfile } from "@/character/storage";
import { mountMyHomePage } from "@/mount-my-home";
import { mountGameShell } from "@/mount-game";
import { mountGmPage } from "@/pages/gm-page";
import { isGmAppPath, normalizeAppPath, parseGmTowerFloorFromPath } from "@/pages/gm-routes";
import { mountGmTowerFloorPage } from "@/pages/gm-tower-floor-page";
import { mountCreatorPage } from "@/pages/creator-page";
import { mountCreatorScenePage } from "@/pages/creator-scene-page";
import { parseCreatorSceneFromPath } from "@/pages/creator-routes";
import { mountHomePage } from "@/pages/home-page";
import { parseDungeonFloorFromPath } from "@/pages/dungeon-routes";
import { homeDungeonFloorHref } from "@/pages/dungeon-routes";
import { startBuildUpdateWatch } from "@/systems/build-update";

type Route =
  | "home"
  | "login"
  | "characterCreate"
  | "shadowSheetGrid"
  | "petSheetGrid"
  | "characterSkills"
  | "myHome"
  | "creator"
  | "creatorScene"
  | "play"
  | "tower"
  | "gm"
  | "gmTowerFloor"
  | "dungeon"
  | "dungeonFloor"
  | "rank";

function resolveRoute(pathname: string): Route {
  const path = normalizeAppPath(pathname);
  if (path === "/login") return "login";
  if (path === "/character") return "myHome";
  if (path === "/character/create") return "characterCreate";
  if (path === "/shadow/sheet-grid") return "shadowSheetGrid";
  if (path === "/pet/sheet-grid") return "petSheetGrid";
  if (path === "/character/skills") return "characterSkills";
  if (path === "/home") return "myHome";
  if (path === "/creator") return "creator";
  if (parseCreatorSceneFromPath(path) !== null) return "creatorScene";
  if (path === "/play") return "play";
  if (path === "/tower") return "tower";
  if (parseGmTowerFloorFromPath(path) !== null) return "gmTowerFloor";
  if (parseDungeonFloorFromPath(path) !== null) return "dungeonFloor";
  if (path === "/dungeon") return "dungeon";
  if (path === "/rank") return "rank";
  if (isGmAppPath(path)) return "gm";
  return "home";
}

function redirectToLogin(nextPath: string): void {
  const next = encodeURIComponent(nextPath);
  window.location.replace(`/login?next=${next}`);
}

/** Fighter HUD theme on player-facing pages; not GM console or fully custom shells. */
const PLAYER_GAME_UI_CLASS = "player-game-ui";

function shouldApplyPlayerGameUi(route: Route): boolean {
  if (route === "gm" || route === "gmTowerFloor") return false;
  if (
    route === "login" ||
    route === "characterCreate" ||
    route === "myHome" ||
    route === "creatorScene"
  ) {
    return false;
  }
  return true;
}

function applyPlayerGameUi(root: HTMLElement, route: Route): void {
  if (!shouldApplyPlayerGameUi(route)) return;
  root.classList.add(PLAYER_GAME_UI_CLASS);
}

async function bootstrap(): Promise<void> {
  const stopBuildWatch = startBuildUpdateWatch();

  const root = document.getElementById("app");
  if (!root) {
    throw new Error("Missing #app");
  }

  const route = resolveRoute(window.location.pathname);
  let teardown: (() => void) | undefined;
  let signedInUid: string | undefined;

  if (route === "login") {
    const user = await waitForAuth();
    if (user) {
      await ensurePlayerCloudSynced(user.uid);
      window.location.replace(resolvePostLoginPath());
      return;
    }
    teardown = mountLoginPage(root);
  } else if (
    route === "characterCreate" ||
    route === "shadowSheetGrid" ||
    route === "petSheetGrid" ||
    route === "characterSkills" ||
    route === "myHome" ||
    route === "creator" ||
    route === "creatorScene" ||
    route === "play" ||
    route === "tower" ||
    route === "gm" ||
    route === "gmTowerFloor" ||
    route === "dungeon" ||
    route === "dungeonFloor" ||
    route === "rank"
  ) {
    const user = await waitForAuth();
    const authReturnPath = normalizeAppPath(window.location.pathname);
    if (!user) {
      redirectToLogin(authReturnPath);
      return;
    }
    await ensurePlayerCloudSynced(user.uid);
    signedInUid = user.uid;
    if (route === "characterCreate") {
      teardown = mountCharacterCreatePage(root, user);
    } else if (route === "shadowSheetGrid") {
      teardown = mountShadowSheetGridPage(root, user);
    } else if (route === "petSheetGrid") {
      teardown = mountPetSheetGridPage(root, user);
    } else if (route === "characterSkills") {
      window.location.replace("/home?skills=1");
      return;
    } else if (route === "myHome") {
      const profile = loadCharacterProfile(user.uid);
      if (!profile) {
        window.location.replace("/character/create");
        return;
      }
      const path = normalizeAppPath(window.location.pathname);
      if (path === "/character") {
        const qs = new URLSearchParams(window.location.search);
        qs.set("character", "1");
        window.location.replace(`/home?${qs.toString()}`);
        return;
      }
      teardown = await mountMyHomePage(root, user);
    } else if (route === "creator") {
      teardown = await mountCreatorPage(root, user);
    } else if (route === "creatorScene") {
      const sceneId = parseCreatorSceneFromPath(window.location.pathname);
      if (!sceneId) {
        window.location.replace("/creator");
        return;
      }
      teardown = await mountCreatorScenePage(root, user, sceneId);
    } else if (route === "gmTowerFloor") {
      const floor = parseGmTowerFloorFromPath(window.location.pathname);
      if (floor === null) {
        window.location.replace("/gm/tower");
        return;
      }
      teardown = await mountGmTowerFloorPage(root, user, floor);
    } else if (route === "dungeonFloor") {
      const floor = parseDungeonFloorFromPath(window.location.pathname) ?? 1;
      window.location.replace(homeDungeonFloorHref(floor));
      return;
    } else if (route === "rank") {
      window.location.replace("/home?rank=1");
      return;
    } else if (route === "gm") {
      teardown = await mountGmPage(root, user);
    } else if (route === "tower") {
      window.location.replace("/home?tower=1");
      return;
    } else if (route === "dungeon") {
      window.location.replace("/home?dungeon=1");
      return;
    } else {
      const profile = loadCharacterProfile(user.uid);
      if (!profile) {
        window.location.replace("/character/create");
        return;
      }
      teardown = mountGameShell(root, user);
    }
  } else {
    const path = normalizeAppPath(window.location.pathname);
    if (path === "/") {
      const user = await waitForAuth();
      if (user) {
        await ensurePlayerCloudSynced(user.uid);
        const profile = loadCharacterProfile(user.uid);
        window.location.replace(profile ? "/home" : "/character/create");
        return;
      }
    }
    teardown = mountHomePage(root);
  }

  applyPlayerGameUi(root, route);

  window.addEventListener(
    "pagehide",
    () => {
      if (signedInUid) flushPlayerCloudPushNow(signedInUid);
      stopBuildWatch();
      teardown?.();
    },
    { once: true },
  );
}

void bootstrap();
